import imageUrl from "@/utils/imageUrl";
import { Carousel, Dropdown } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { EVENT } from "../../constants/event";
import setCloseAds from "../../utils/closeAds";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

function RecommendCarouselCard({ data, record, onClose }) {
  const [index, setIndex] = useState(0);
  const carouselRef = useRef(null);

  const onAfterChange = useCallback((index) => {
    setIndex(index);
  }, []);

  const onNext = useCallback(() => {
    carouselRef.current?.next();
  }, []);

  const onPrev = useCallback(() => {
    carouselRef.current?.prev();
  }, []);

  const list = useMemo(() => {
    return data?.list?.map((item, index) => (
      <a
        target="_blank"
        href={item?.link}
        key={index}
        rel={"noreferrer nofollow"}
        data-gd-click="resource_loc_click"
        data-gd-expose="resource_loc_expose"
        data-resource-type={item.type}
        data-resource-id={record?.material?.id}
        data-resource-title={record?.promotion?.name}
        data-content-source={record?.position?.alias}
      >
        <If condition={item.type === "video"}>
          <Then>
            <video
              src={item.src}
              loop
              autoPlay
              preload="metadata"
              muted
            ></video>
          </Then>
          <Else>
            <img src={item.src} alt={data?.title} />
          </Else>
        </If>
      </a>
    ));
  }, [data, record]);

  const menu = useMemo(() => {
    return (
      <div className={styles.menu}>
        <a
          className={styles.menuItem}
          onClick={onClose}
          data-gd-click="button_click"
          data-button-name="关闭广告"
        >
          <span className={styles.text}>关闭广告</span>
        </a>
      </div>
    );
  }, [onClose]);

  return (
    <div className={styles.carousel}>
      <div
        className={styles.container}
        style={{ height: `${(data?.height / data?.width) * 240}px` }}
      >
        <When condition={data?.list?.length > 1}>
          <button
            className={styles.prev}
            disabled={index <= 0}
            onClick={onPrev}
          >
            <HBIcon type="ic_chevron_left" />
          </button>
        </When>
        <If condition={data?.list?.length > 1}>
          <Then>
            <Carousel
              ref={carouselRef}
              dots={{ className: styles.dot }}
              autoplay
              afterChange={onAfterChange}
            >
              {list}
            </Carousel>
          </Then>
          <Else>{list}</Else>
        </If>
        <When condition={data?.list?.length > 1}>
          <button
            className={styles.next}
            disabled={index >= data?.list?.length - 1}
            onClick={onNext}
          >
            <HBIcon type="ic_chevron_right" />
          </button>
        </When>
      </div>
      <When condition={data?.title}>
        <div className={styles.title} title={data?.title}>
          {data?.title}
        </div>
      </When>
      <div className={styles.desc}>
        <div className={styles.left}>
          <When condition={data?.logo}>
            <div className={styles.logo}>
              <img src={data?.logo} alt={data?.name} />
            </div>
          </When>
          <When condition={data?.name}>
            <div className={styles.name}>{data?.name}</div>
          </When>
          <When condition={record.material.tag}>
            <div className={styles.label}>
              <div className={styles.text}>{record.material.tag}</div>
            </div>
          </When>
        </div>
        <div className={styles.right}>
          <When condition={record.showcase.closable}>
            <Dropdown overlay={menu} placement="bottomRight">
              <div className={styles.close}>
                <HBIcon type="ic_more" />
              </div>
            </Dropdown>
          </When>
        </div>
      </div>
      <When condition={record.material.tracker}>
        <img
          src={record.material.tracker}
          alt="埋点"
          style={{ display: "none" }}
        />
      </When>
    </div>
  );
}

export default function RecommendCard({ record, style, onDelete }) {
  const onClose = useCallback(
    (evt) => {
      evt.preventDefault();
      const pinId = record.pin_id;
      const boardId = record.board_id;
      // 判断是否是采集还是画板
      if (pinId) {
        window.dispatchEvent(
          new CustomEvent(EVENT.DELETE_PIN, { detail: pinId })
        );
      } else if (boardId) {
        onDelete(boardId);
      }

      setCloseAds(record.material);
    },
    [record, onDelete]
  );

  if (record.material.type === "html") {
    try {
      const data = JSON.parse(record.material.data);
      if (data.type === "carousel") {
        return (
          <RecommendCarouselCard
            data={data}
            record={record}
            onClose={onClose}
          />
        );
      }

      return null;
    } catch (err) {
      return (
        <div className={styles.html}>
          <div
            dangerouslySetInnerHTML={{
              __html: record.material.data,
            }}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type="html"
            data-resource-id={record?.material?.id}
            data-resource-title={record?.promotion?.name}
            data-content-source={record?.position?.alias}
          ></div>
          <When condition={record.showcase.closable}>
            <div
              className={styles.close}
              onClick={onClose}
              data-gd-click="button_click"
              data-button-name="关闭广告"
            >
              <HBIcon type="ic_close" />
            </div>
          </When>
          <When condition={record.material.tracker}>
            <img
              src={record.material.tracker}
              alt="埋点"
              style={{ display: "none" }}
            />
          </When>
        </div>
      );
    }
  } else if (record.material.type === "image") {
    const image = record.material.data;
    const imgUrl =
      typeof image === "string"
        ? image
        : imageUrl(
            {
              bucket: image.bucket_name,
              key: image.bucket_key,
            },
            { width: 480 }
          );
    return (
      <a
        className={styles.img}
        target="_blank"
        href={record.material.link}
        style={{
          height: Math.round(
            (record.material.height * 240) / record.material.width
          ),
          ...style,
        }}
        data-gd-click="resource_loc_click"
        data-gd-expose="resource_loc_expose"
        data-resource-type="图片"
        data-resource-id={record?.material?.id}
        data-resource-title={record?.promotion?.name}
        data-content-source={record?.position?.alias}
        data-resource-func={
          record.material.link.includes("from=auto") ? "智能投放" : "广告"
        }
        rel="noreferrer nofollow"
      >
        <When condition={record.showcase.closable}>
          <div
            className={styles.close}
            onClick={onClose}
            data-gd-click="button_click"
            data-button-name="关闭广告"
          >
            <HBIcon type="ic_close" />
          </div>
        </When>
        <img src={imgUrl} alt={record.material.tag} />
        <When condition={record.material.tag}>
          <div className={styles.label}>{record.material.tag}</div>
        </When>
        <When condition={record.material.tracker}>
          <img
            src={record.material.tracker}
            alt="埋点"
            style={{ display: "none" }}
          />
        </When>
      </a>
    );
  }
  return null;
}
